import { ReactElement, useState } from 'react';
import { useQuery } from 'react-query';

import { Button } from 'antd';

import { OS_TYPE } from '@common/const';
import { alertError } from '@common/modal';
import { isAppDevice } from '@common/webview';
import { css } from '@emotion/react';
import { FraudResultType } from '@type/web/fraud';

import useGoBack from '@lib/hook/useGoBack';
import { palette } from '@lib/styles/palette';

import * as FraudAPI from '@api/base/fraud';

import LocalResultDetail from '@components/web/Fraud/FraudSearchResult/LocalResultDetail/LocalResultDetail';
import CommonResultText from '@components/web/Fraud/FraudSearchResult/ResultText/CommonResultText';
import LocalResultText from '@components/web/Fraud/FraudSearchResult/ResultText/LocalResultText';
import {
  emphasizeText,
  underLineText,
} from '@components/web/Fraud/FraudSearchResult/fraudSearchResult.style';

import InfoPopUp from '../common/InfoPopUp';

interface FraudSearchResultProps {
  result: FraudResultType;
}

function FraudSearchResult({ result }: FraudSearchResultProps): ReactElement {
  const [onToggle, setOnToggle] = useState<boolean>(false);
  const [localDropdown, setLocalDropdown] = useState<boolean>(true);
  const { cyberCop, keyword, maskingKeyword, theCheat, type, local } = result;

  const goBack = useGoBack();

  const openLocalDropdown = () => {
    setLocalDropdown(true);
  };

  const closeLocalDropdown = () => {
    setLocalDropdown(false);
  };

  const handleToggle = () => {
    setOnToggle(!onToggle);
  };

  const { isFetching, data } = useQuery(
    'fraudDetail',
    () => {
      const osType = isAppDevice() ? OS_TYPE.APP : OS_TYPE.WEB;
      setLocalDropdown(true);
      return FraudAPI.getFraudLocalDetail({ keyword, type, osType });
    },
    {
      enabled: !!local.count && local.count > 0,
      onError: () => {
        alertError({
          onOk: closeLocalDropdown,
        });
      },
    },
  );

  return (
    <div css={wrap}>
      <div css={resultTitleWrap}>
        <p>({maskingKeyword})</p>
        <p>피해 사례 조회</p>
      </div>
      <div css={searchPeriod}>
        <p>검색 기간 : 최근 3개월</p>
      </div>
      <dl css={resultWrap}>
        <LocalResultText
          result={{
            name: '중고나라',
            count: local.count,
            isOpen: localDropdown,
            openLocalDropdown,
          }}
        />
        {local.count > 0 && localDropdown ? (
          <LocalResultDetail
            detail={{
              isFetching,
              reportPath: local.report_path,
              localDetail: data,
              closeLocalDropdown,
            }}
          />
        ) : null}
        <CommonResultText name="더치트" count={theCheat.count}>
          &#40;
          <span css={emphasizeText(true)}>최근 2일 이내</span> 또는{' '}
          <span css={emphasizeText(true)}>검증이 완료되지 않은</span> 더치트
          신고 접수 건은 결과에 반영되지 않을 수 있습니다.&#41;{' '}
        </CommonResultText>
        <CommonResultText
          name={'사이버범죄\n신고시스템'}
          count={cyberCop.count}
        >
          &#40;
          <span css={emphasizeText(true)}>최근 3개월</span> 동안{' '}
          <span css={emphasizeText(true)}>3회 미만</span> 사이버범죄 신고시스템
          접수 건은 결과에 반영되지 않을 수 있습니다.&#41;{' '}
        </CommonResultText>
      </dl>
      <div css={buttonStyle}>
        <Button onClick={goBack}>다시조회하기</Button>
        <Button onClick={handleToggle}>이의제기하기</Button>
      </div>
      {onToggle && (
        <InfoPopUp>
          <p>
            이의제기 신청은{' '}
            <span css={[underLineText(null), bold]}>
              jncsteam@joonggonara.co.kr
            </span>
            <br />로 접수 부탁드립니다.
          </p>
          <button type="button" aria-label="confirm" onClick={handleToggle}>
            확인
          </button>
        </InfoPopUp>
      )}
    </div>
  );
}

const wrap = css`
  font-size: 12px;
  padding: 10px 20px;
  line-height: 20px;
`;

const resultTitleWrap = css`
  font-size: 20px;
  font-weight: 700;
  p {
    padding-top: 10px;
  }
`;

const searchPeriod = css`
  font-size: 13px;
  font-weight: 500;
  text-align: right;
  padding-bottom: 5px;
`;

const resultWrap = css`
  display: flex;
  flex-wrap: wrap;
  border-top: 1px solid #e0e0e0;
  dt {
    display: flex;
    flex: 1 0 26%;
    padding: 28px 12px 28px 4px;
    font-size: 14px;
    font-weight: 700;
    border-bottom: 1px solid #e0e0e0;
    white-space: pre-line;
  }
  button {
    display: flex;
    justify-content: space-between;
    background: none;
  }
  svg {
    margin-left: 8px;
  }
`;

const buttonStyle = css`
  button {
    width: 100%;
    height: 46px;
    margin: 40px 0 10px;
    background: none;
    border: 1px solid ${palette.orange9};
    border-radius: 4px;
    font-size: 16px;
    color: ${palette.orange9};
    font-weight: 700;
    text-align: center;
    &:last-child {
      margin-top: 10px;
      margin-bottom: 50px;
      color: ${palette.orange9};
      border: 1px solid ${palette.orange9};
    }
  }
`;

const bold = css`
  font-weight: 700;
`;
export default FraudSearchResult;
